/*
 * Offer a plan (owner, 2026-11-02): the text a one-time home client gets with the prices for their home, before it goes
 * out. Three ways to open it (after a clean, their cleaner told us, or they asked), and the office can change the words.
 * Their link opens a quote where they pick the schedule and a day with the cleaner they had last, or let us pick the
 * soonest day. It stays under Offers in My Trades. Used on the job page, the client page and the Inbox.
 */
import type { PlanOfferOpener } from "@steward/shared";
import { useState } from "react";
import { usePlanOffer, useSendPlanOffer } from "../api/hooks";
import { Choice, FormField } from "./fields";
import { moneyExact } from "./format";
import { Modal, useToast } from "./overlays";
import { Banner, Button, Details, Skeleton } from "./parts";
import { errText } from "./work";

export function PlanOfferSend({
  clientId,
  onClose,
  opener: start,
  leadId,
  onSent,
}: {
  clientId: string;
  onClose: () => void;
  /** How it starts, when the place it opens from knows why (a request, a text). Else what fits best. */
  opener?: PlanOfferOpener | undefined;
  /** The request it answers: it moves to Quote sent with the offer on it. */
  leadId?: string | null | undefined;
  onSent?: (() => void) | undefined;
}) {
  const offer = usePlanOffer(clientId);
  const send = useSendPlanOffer();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [opener, setOpener] = useState<PlanOfferOpener | null>(null);
  const [text, setText] = useState<string | null>(null);
  const o = offer.data;
  // Opens on why they are getting it: what the page knew, else their request or text, else after a clean.
  const picked = opener ?? start ?? o?.suggested ?? "after";
  const shown = text ?? o?.texts.find((t) => t.opener === picked)?.text ?? o?.text ?? "";
  const go = async () => {
    setError(null);
    if (!shown.trim()) {
      setError("Type the text.");
      return;
    }
    try {
      await send.mutateAsync({ clientId, text: shown, ...(leadId ? { leadId } : {}) });
      toast("Offer sent");
      onSent?.();
      onClose();
    } catch (e) {
      setError(errText(e));
    }
  };
  return (
    <Modal
      title="Offer a plan"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={send.isPending} disabled={!o} onClick={() => void go()}>
            Send text
          </Button>
        </>
      }
    >
      <div className="stack">
        {offer.error ? <Banner type="notice">{errText(offer.error)}</Banner> : null}
        {error ? <Banner type="error">{error}</Banner> : null}
        {!o && !offer.error ? <Skeleton rows={3} /> : null}
        {o ? (
          <>
            <p className="muted small">
              For their home, {o.home}.{" "}
              {o.firstVisitCents === null
                ? "They had a clean in the last 30 days, so the first visit is at the plan price."
                : `The first visit is ${moneyExact(o.firstVisitCents)}, then the plan price.`}
              {o.cleaner ? ` They see ${o.cleaner}'s open days first, so ${o.cleaner} keeps coming.` : ""}
            </p>
            <Details rows={o.prices.map((p) => [p.label, `${moneyExact(p.perVisitCents)} a visit`] as [string, string])} />
            <Choice
              label="How it starts"
              value={picked}
              onChange={(v) => {
                setOpener(v);
                setText(null);
              }}
              options={o.texts.map((t) => ({ value: t.opener, label: t.label }))}
            />
            <FormField label="The text" description="LINK becomes their own link when it sends.">
              <textarea rows={5} maxLength={640} value={shown} onChange={(e) => setText(e.target.value)} />
            </FormField>
            <p className="muted small">
              {o.openQuoteId
                ? "They have an open offer from the last 30 days. This sends the same link again."
                : "Their link opens a page where they pick the schedule and a day, or let us pick the soonest day. It stays under Offers in My Trades."}
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
